import styled, { css } from 'styled-components';
import {
  fmtRequestDate,
  functionLabel,
  readMtoSupplyPayload,
  type FunctionRequest,
} from '../types';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  font-size: 13.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Facts = styled.dl`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(4)}`};
  margin: 0;

  dt {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  dd {
    margin: 0;
    font-weight: 600;
    overflow-wrap: anywhere;
  }

  @media (max-width: 520px) {
    grid-template-columns: minmax(0, 1fr);

    dd {
      margin-bottom: ${({ theme }) => theme.spacing(2)};
    }
  }
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Plain = styled.ul`
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing(5)};

  li + li {
    margin-top: ${({ theme }) => theme.spacing(0.5)};
  }
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 400;
`;

const Materials = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
    /* Количество и наименование — свободный текст: переносим, а не распираем
       таблицу на узком экране. */
    overflow-wrap: anywhere;
  }

  th {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  td.qty {
    width: 30%;
  }
`;

const More = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Quote = styled.blockquote<{ $clamp: boolean }>`
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing(3)};
  border-left: 2px solid ${({ theme }) => theme.colors.border};
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  /* В списке — первые строки комментария, целиком — в карточке заявки. */
  ${({ $clamp }) =>
    $clamp &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}
`;

interface Props {
  request: FunctionRequest;
  /** Для строки списка: материалов — первые несколько, остальное в карточке. */
  compact?: boolean;
}

const COMPACT_MATERIALS = 5;

/**
 * Что просят в заявке в снабжение — по-человечески: объект, к какому сроку,
 * от кого, документы РД (шифр, название, ревизия), материалы списком и
 * комментарий. Данные — снимок, сделанный сервером при подаче: у снабженца
 * нет доступа к ПТО, поэтому живые ссылки на документы здесь не нужны.
 */
export function MtoSupplySummary({ request, compact = false }: Props) {
  const p = readMtoSupplyPayload(request.payload);
  const needBy = p.need_by ?? request.due_date;
  const materials = compact ? p.materials.slice(0, COMPACT_MATERIALS) : p.materials;
  const hiddenMaterials = p.materials.length - materials.length;

  return (
    <Wrap>
      <Facts>
        <dt>Объект</dt>
        <dd>{p.project_name ?? <Muted>не указан</Muted>}</dd>
        <dt>Нужно к</dt>
        <dd>{needBy ? fmtRequestDate(needBy) : <Muted>срок не указан</Muted>}</dd>
        <dt>От кого</dt>
        <dd>
          {p.initiator_name ? (
            <>
              {p.initiator_name} <Muted>· {functionLabel(request.source_function)}</Muted>
            </>
          ) : (
            functionLabel(request.source_function)
          )}
        </dd>
      </Facts>

      {p.documents.length > 0 && (
        <div>
          <Label>Документы РД — материалы нужны по ним</Label>
          <Plain>
            {p.documents.map((d) => (
              <li key={d.revision_id}>
                <b>{d.code}</b>
                {d.title && <> — {d.title}</>}
                {d.revision && <Muted> · {d.revision}</Muted>}
              </li>
            ))}
          </Plain>
        </div>
      )}

      {materials.length > 0 && (
        <div>
          <Label>Материалы</Label>
          <Materials>
            <thead>
              <tr>
                <th>Наименование</th>
                <th>Сколько</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((m, i) => (
                <tr key={`${i}-${m.name}`}>
                  <td>{m.name}</td>
                  <td className="qty">{[m.quantity, m.unit].filter(Boolean).join(' ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </Materials>
          {hiddenMaterials > 0 && <More>и ещё {hiddenMaterials} — в карточке заявки</More>}
        </div>
      )}

      {p.comment && <Quote $clamp={compact}>{p.comment}</Quote>}
    </Wrap>
  );
}
